// 🔧 use_promotion_posts.js
// ✅ Purpose: React hook for managing Promotion Posts
// ✅ Features:
// - Loads Promotion Posts
// - Updates Promotion Posts
// - Deletes Promotion Posts
// - Refreshes the Draft Manager
// - Keeps React pages free from service implementation details
// - Future-ready for approval, scheduling, publishing, and analytics
// - Fully block-commented for clarity and future devs

import { useCallback, useEffect, useState } from "react";

import {
  approvePromotionPost,
  deletePromotionPost,
  getPromotionPost,
  getPromotionPosts,
  savePromotionPostChanges,
  schedulePromotionPost,
  updatePromotionPost,
} from "../services/promotion_post_service";

const postListCache = new Map();

/**
 * ============================================================================
 * usePromotionPosts
 * ============================================================================
 *
 * Central hook for interacting with Promotion Posts.
 *
 * Future additions:
 * - Approve posts
 * - Schedule posts
 * - Publish posts
 * - Archive posts
 */
export const usePromotionPosts = ({ eventId = null, enabled = true, loadList = true } = {}) => {
  const cacheKey = eventId || "internal_lgv";
  const [posts, setPosts] = useState(() => postListCache.get(cacheKey) || []);
    const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(loadList);

  const [saving, setSaving] = useState(false);

  const invalidatePostsCache = () => {
    postListCache.delete(cacheKey);
  };

  /**
   * ==========================================================================
   * Schedule Post
   * ==========================================================================
   *
   * Schedules an Approved Promotion Post
   * for future publishing.
   */
  const schedulePost = async ({
    postId,
    scheduledAt,
    updatedBy = "",
  }) => {

    setSaving(true);

    try {

      await schedulePromotionPost({

        postId,

        scheduledAt,

        updatedBy,

      });

      if (loadList) await refreshPosts({ force: true });

    } finally {

      setSaving(false);

    }

  };

  /**
   * ==========================================================================
   * Approve Post
   * ==========================================================================
   *
   * Moves a Promotion Post from Draft
   * to Approved status.
   */
  const approvePost = async (
    postId,
    updatedBy = ""
  ) => {

    setSaving(true);

    try {

      await approvePromotionPost(
        postId,
        updatedBy
      );

      if (loadList) await refreshPosts({ force: true });

    } finally {

      setSaving(false);

    }

  };

  /**
   * ==========================================================================
   * Refresh Posts
   * ==========================================================================
   */

  const refreshPosts = useCallback(async ({ force = false } = {}) => {
    setLoading(true);

    try {
      if (!enabled || !loadList) return;

      if (!force && postListCache.has(cacheKey)) {
        setPosts(postListCache.get(cacheKey));
        return;
      }

      const results = await getPromotionPosts({ eventId });

      setPosts(results);
      postListCache.set(cacheKey, results);
    } catch (error) {
      console.error(
        "[Promotion Engine] Failed loading posts:",
        error
      );
    } finally {
      setLoading(false);
    }
  }, [cacheKey, enabled, eventId, loadList]);

  /**
   * ==========================================================================
   * Update Post
   * ==========================================================================
   */

  const updatePost = async (
    postId,
    updates,
    updatedBy = ""
  ) => {
    setSaving(true);

    try {
      await updatePromotionPost(
        postId,
        updates,
        updatedBy
      );

      if (loadList) await refreshPosts({ force: true });
    } finally {
      setSaving(false);
    }
  };

  /**
     * ==========================================================================
     * Load Single Post
     * ==========================================================================
     */

    const loadPost = useCallback(async (
    postId
    ) => {

    const post =
        await getPromotionPost(postId);

    setSelectedPost(post);

    return post;

    }, []);

    /**
     * ==========================================================================
     * Save Post Changes
     * ==========================================================================
     */

    const savePostChanges = async (
    postId,
    updates,
    updatedBy = ""
    ) => {

    setSaving(true);

    try {

        await savePromotionPostChanges(
        postId,
        updates,
        updatedBy
        );

        if (loadList) await refreshPosts({ force: true });

        const updated =
        await getPromotionPost(postId);

        setSelectedPost(updated);

        return updated;

    } finally {

        setSaving(false);

    }

    };

    /**
     * ==========================================================================
     * Clear Selected Post
     * ==========================================================================
     */

    const clearSelectedPost = () => {

    setSelectedPost(null);

    };

  /**
   * ==========================================================================
   * Delete Post
   * ==========================================================================
   */

  const deletePost = async (postId) => {
    setSaving(true);

    try {
      await deletePromotionPost(postId);

      if (loadList) await refreshPosts({ force: true });
    } finally {
      setSaving(false);
    }
  };

  /**
   * ==========================================================================
   * Initial Load
   * ==========================================================================
   */

  useEffect(() => {
    if (enabled && loadList) refreshPosts();
  }, [enabled, loadList, refreshPosts]);

    return {

    posts,

    selectedPost,

    loading,

    saving,

    refreshPosts,

    loadPost,

    savePostChanges,
    
    schedulePost,

    clearSelectedPost,

    updatePost,

    deletePost,

    approvePost,

    invalidatePostsCache,
  

    };

};
